// ============================================================
//  src/pages/CategoryPage.jsx  — React Router v6 migration
// ============================================================

import { useState, useCallback, useEffect } from "react";   // ← added useEffect
import { useNavigate, useParams } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { Toast, SectionTitle } from "../components/UI";
import useProducts from "../hooks/useProducts";
import { addItemToCart } from "../utils/helpers";
import { supabase } from "../utils/supabase";               // ← added
import config from "../config";

// slug → display name map (same as CATEGORIES in App.jsx)
const SLUG_TO_NAME = {
  "millets":            "Millets",
  "traditional-rice":   "Traditional Rice",
  "dry-fruits-nuts":    "Dry Fruits & Nuts",
  "sweeteners":         "Sweeteners",
};

const CATEGORY_META = {
  "Millets":           { emoji: "🌾", color: "#2d5a27", lightColor: "#eef7ee", description: "Nutrient-rich, gluten-free ancient grains straight from organic farms. High in fibre, protein, and minerals.", banner: "/images/y.jpg" },
  "Traditional Rice":  { emoji: "🍚", color: "#2c3e50", lightColor: "#eaf0f6", description: "Heirloom and heritage rice varieties packed with antioxidants, rich flavour, and superior nutrition.",           banner: "/images/x.jpg" },
  "Dry Fruits & Nuts": { emoji: "🥜", color: "#a04000", lightColor: "#fdf3e7", description: "Premium quality dry fruits and nuts. No preservatives, no added salt — just pure natural goodness.",             banner: "/images/Nuts.jpg" },
  "Sweeteners":        { emoji: "🍯", color: "#d4830a", lightColor: "#fef9ec", description: "Natural, unrefined sweeteners with low glycemic index. Healthier alternatives to refined sugar.",                banner: "/images/sweetners.jpg" },
};

const SORT_OPTIONS = [
  { label: "Default",           value: "default"    },
  { label: "Price: Low → High", value: "price_asc"  },
  { label: "Price: High → Low", value: "price_desc" },
  { label: "Top Rated",         value: "rating"     },
];

const CATEGORY_CONTENT = {
  "Millets":           { heading: "Organic Unpolished Millets Online",          highlights: ["Gluten Free", "Stone Ground", "No Preservatives", "Farm Direct"], paragraphs: ["Millets are one of the earliest grains that humans cultivated before rice, and their power-packed nutrient properties make them a healthy food choice for decades. Rich in fibre, protein, and essential minerals, millets are a true superfood from nature.", "RK Farm Foods sources unpolished millets directly from trusted organic farmers across India. Every grain is carefully cleaned, stone-ground where needed, and packed fresh — with no chemicals, no polishing, and no preservatives.", "Enjoy the authentic flavours of organic millet food, rich in high nutritional value. Now, you can easily get the savory taste of unpolished millets online with RK Farm Foods — delivered straight to your door."], image: "/images/millet-bajra.jpg" },
  "Traditional Rice":  { heading: "Heritage & Traditional Rice Varieties Online", highlights: ["Heirloom Varieties", "Unpolished", "Antioxidant Rich", "Farm Direct"], paragraphs: ["Traditional rice varieties have been cultivated in India for thousands of years, long before modern hybrid strains took over. These heirloom grains carry distinct flavours, natural aromas, and superior nutritional profiles that polished white rice simply cannot match.", "RK Farm Foods works directly with farmers who preserve these ancient varieties — from Manipuri black rice to red matta rice — ensuring each grain reaches you unpolished, chemical-free, and full of natural goodness.", "Rediscover the authentic taste of India's rice heritage. Rich in antioxidants, fibre, and minerals, our traditional rice varieties are perfect for everyday cooking and mindful eating."], image: "/images/black-rice.jpg" },
  "Dry Fruits & Nuts": { heading: "Premium Natural Dry Fruits & Nuts Online",    highlights: ["No Added Salt", "Hand Sorted", "Naturally Dried", "No Preservatives"], paragraphs: ["Dry fruits and nuts are nature's most concentrated source of energy, healthy fats, and essential minerals. For centuries, they have been a cornerstone of Indian diets — valued for their nourishment, flavour, and long shelf life.", "RK Farm Foods sources only premium-grade dry fruits and nuts from the best regions across India — Goa cashews, Kashmiri walnuts, and more. Every batch is hand-sorted, naturally dried, and packed without any added salt, oil, or preservatives.", "Whether you snack on them raw, add them to sweets, or blend them into smoothies — our dry fruits and nuts deliver unmatched freshness and quality in every bite."], image: "/images/allnuts.jpg" },
  "Sweeteners":        { heading: "Natural & Unrefined Sweeteners Online",       highlights: ["Low Glycemic Index", "Unrefined", "No Chemicals", "Mineral Rich"], paragraphs: ["Natural sweeteners have been an integral part of Indian cooking and Ayurveda for centuries. Unlike refined white sugar, natural sweeteners retain their minerals, enzymes, and complex flavours — making them a far healthier choice for everyday use.", "RK Farm Foods offers a curated range of natural sweeteners — from traditional jaggery powder to raw honey — all sourced directly from farmers and producers who follow time-honoured methods of preparation without chemical processing.", "Make the switch to natural today. Our sweeteners carry a low glycemic index, rich mineral content, and authentic sweetness that transforms every dish — from your morning chai to festive sweets."], image: "/images/sugarcane.jpg" },
};

export default function CategoryPage({ cart, setCart }) {
  const navigate          = useNavigate();
  const { slug }          = useParams();
  const category          = SLUG_TO_NAME[slug] || slug;

  const [toast, setToast]     = useState(null);
  const [sort, setSort]       = useState("default");
  const [stockMap, setStockMap] = useState({});             // ← added
  const { products, loading } = useProducts();

  const meta             = CATEGORY_META[category] || { emoji: "🛒", color: config.colors.primary, lightColor: "#f0f7ee", description: "", banner: null };
  const categoryProducts = products.filter((p) => p.category === category);
  const sorted           = [...categoryProducts].sort((a, b) => {
    if (sort === "price_asc")  return a.price - b.price;
    if (sort === "price_desc") return b.price - a.price;
    if (sort === "rating")     return b.rating - a.rating;
    return 0;
  });

  // Fetch stock for this category's products + realtime updates
  useEffect(() => {
    // 1. Bulk initial fetch — all rows at once, filter client-side
    supabase
      .from('product_stock')
      .select('product_id, status, quantity')
      .then(({ data }) => {
        if (!data) return;
        const map = {};
        data.forEach((row) => { map[String(row.product_id)] = row; });
        setStockMap(map);
      });

    // 2. Realtime — reflect any UPDATE instantly across all cards
    const channel = supabase
      .channel(`category-stock-${slug}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'product_stock' },
        (payload) => {
          setStockMap((prev) => ({
            ...prev,
            [String(payload.new.product_id)]: payload.new,
          }));
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [slug]);

  const handleAddToCart = useCallback((product) => {
    setCart((prev) => addItemToCart(prev, product));
    setToast(`${product.name} added!`);
    navigate("/cart");
    window.scrollTo(0, 0);
  }, [setCart, navigate]);

  function handleBuyNow(product, e) {
    if (e) e.stopPropagation();
    setCart((prev) => addItemToCart(prev, product));
    navigate("/cart");
    window.scrollTo(0, 0);
  }

  function openProduct(product) {
    navigate(`/product/${product.id}`);
    window.scrollTo(0, 0);
  }

  return (
    <div style={{ minHeight: "100vh", background: "#faf8f5" }}>
      <style>{`
        .category-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: center; }
        .category-info-image { position: relative; }
        .category-info-heading { font-family: 'Playfair Display', serif; font-size: 32px; font-weight: 900; color: #1a1a1a; margin: 0 0 24px; line-height: 1.2; }
        @media (max-width: 640px) {
          .category-info-grid { grid-template-columns: 1fr !important; gap: 24px !important; }
          .category-info-image { display: none !important; }
          .category-info-heading { font-size: 22px !important; }
        }
      `}</style>

      {/* Hero Banner */}
      <div style={{ position: "relative", overflow: "hidden", minHeight: 220 }}>
        {meta.banner && <div style={{ position: "absolute", inset: 0, backgroundImage: `url('${meta.banner}')`, backgroundSize: "cover", backgroundPosition: "center", filter: "brightness(0.45)" }} />}
        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(135deg, ${meta.color}ee 0%, ${meta.color}99 100%)` }} />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 900, margin: "0 auto", padding: "36px 16px 28px" }}>
          <div style={{ marginBottom: 14 }}>
            <button onClick={() => { navigate("/"); window.scrollTo(0, 0); }}
              style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", color: "#fff", borderRadius: 20, padding: "4px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              ← Home
            </button>
          </div>
          <div style={{ fontSize: 40, marginBottom: 10 }}>{meta.emoji}</div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(26px, 7vw, 40px)", fontWeight: 900, color: "#fff", margin: "0 0 10px", textShadow: "0 2px 12px rgba(0,0,0,0.2)" }}>
            {category}
          </h1>
          <p style={{ color: "rgba(255,255,255,0.9)", fontSize: 14, maxWidth: 560, lineHeight: 1.6, margin: 0 }}>{meta.description}</p>
          <div style={{ marginTop: 18, display: "inline-block", background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 20, padding: "5px 16px", color: "#fff", fontSize: 13, fontWeight: 700 }}>
            {loading ? "Loading..." : `${categoryProducts.length} Products`}
          </div>
        </div>
      </div>

      {/* Info Content */}
      {CATEGORY_CONTENT[category] && (
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "40px 16px 0" }}>
          <div className="category-info-grid">
            <div>
              <h2 className="category-info-heading">{CATEGORY_CONTENT[category].heading}</h2>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 24 }}>
                {CATEGORY_CONTENT[category].highlights.map((h) => (
                  <span key={h} style={{ background: meta.lightColor, color: meta.color, border: `1px solid ${meta.color}33`, borderRadius: 20, padding: "4px 14px", fontSize: 12, fontWeight: 800 }}>✓ {h}</span>
                ))}
              </div>
              {CATEGORY_CONTENT[category].paragraphs.map((para, i) => (
                <p key={i} style={{ color: "#555", fontSize: 15, lineHeight: 1.8, marginBottom: 14 }}>{para}</p>
              ))}
            </div>
            <div className="category-info-image">
              <div style={{ width: 340, height: 340, borderRadius: "50%", background: meta.lightColor, border: `2px dashed ${meta.color}44`, margin: "0 auto", position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <img src={CATEGORY_CONTENT[category].image} alt={category} style={{ width: 260, height: 260, borderRadius: "50%", objectFit: "cover", boxShadow: "0 20px 50px rgba(0,0,0,0.12)" }} onError={(e) => { e.target.style.display = "none"; }} />
                <div style={{ position: "absolute", top: -10, left: -10, width: 90, height: 90, borderRadius: "50%", background: meta.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}>{meta.emoji}</div>
                <div style={{ position: "absolute", bottom: -10, right: -10, width: 80, height: 80, borderRadius: "50%", background: "#fff", border: `3px solid ${meta.color}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}>🌿</div>
              </div>
              <div style={{ display: "flex", justifyContent: "center", gap: 24, marginTop: 24 }}>
                {[{ num: "100%", label: "Organic" }, { num: "0", label: "Preservatives" }, { num: "Farm", label: "Direct" }].map((s) => (
                  <div key={s.label} style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 20, fontWeight: 900, color: meta.color }}>{s.num}</div>
                    <div style={{ fontSize: 11, color: "#888", fontWeight: 700 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={{ height: 1, background: `linear-gradient(90deg, transparent, ${meta.color}33, transparent)`, margin: "40px 0 0" }} />
        </div>
      )}

      {/* Products */}
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px 16px 80px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>
          <SectionTitle>All {category}</SectionTitle>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, color: "#888", fontWeight: 600 }}>Sort:</span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {SORT_OPTIONS.map((opt) => (
                <button key={opt.value} onClick={() => setSort(opt.value)} style={{ padding: "5px 12px", borderRadius: 20, border: `2px solid ${sort === opt.value ? meta.color : config.colors.cardBorder}`, background: sort === opt.value ? meta.color : "#fff", color: sort === opt.value ? "#fff" : "#666", fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(155px, 46%), 1fr))", gap: 14 }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} style={{ height: 240, borderRadius: 14, background: "linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
            ))}
            <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
          </div>
        )}

        {!loading && sorted.length === 0 && (
          <div style={{ textAlign: "center", padding: "80px 20px", color: "#aaa" }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>{meta.emoji}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#666", marginBottom: 8 }}>No products found</div>
            <p style={{ fontSize: 14 }}>We're restocking soon. Check back shortly!</p>
            <button onClick={() => { navigate("/"); window.scrollTo(0, 0); }}
              style={{ marginTop: 20, background: meta.color, color: "#fff", border: "none", borderRadius: 24, padding: "10px 28px", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>
              Back to Home
            </button>
          </div>
        )}

        {!loading && sorted.length > 0 && (
          <div className="product-grid" role="list" aria-label={`${category} products`}>
            {sorted.map((p) => (
              <div key={p.id} className="product-cell" role="listitem">
                <ProductCard
                  product={p}
                  stockInfo={stockMap[String(p.id)]}        // ← wired up
                  onClick={() => openProduct(p)}
                  onAddToCart={handleAddToCart}
                  onBuyNow={handleBuyNow}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}
